import { useState, useEffect, useCallback } from 'react'
import { adminService } from '../../services/adminService'
import {
  CreditCard,
  Plus,
  Edit2,
  Save,
  X,
  Loader2,
  AlertTriangle,
  RefreshCw,
  BarChart3,
  Settings,
  UserPlus,
  Check,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
} from 'lucide-react'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface SubscriptionPlan {
  id: string
  name: string
  userType: 'vendor' | 'lender'
  price: number
  billingCycle: 'monthly' | 'annual' | 'one_time'
  features: string[]
  scoreRefreshFrequency: string
  documentUploadLimit: number
  scoreHistoryDepth: number
  aiAnalysisLevel: 'none' | 'basic' | 'advanced' | 'full'
  exportPermissions: string[]
  monthlyLookups: number | null
  apiRateLimit: number | null
  isActive: boolean
  createdAt?: string
  updatedAt?: string
}

interface SubscriptionAnalytics {
  vendorsPerTier: Record<string, number>
  monthlyChanges: { upgrades: number; downgrades: number; month: string }[]
  revenueByTier: Record<string, number>
  churnRate: number
}

interface SubscriptionRule {
  id?: string
  ruleType: string
  config: Record<string, any>
  isActive: boolean
}

type TabType = 'plans' | 'assign' | 'analytics' | 'rules'

interface PlanFormData {
  name: string
  userType: 'vendor' | 'lender'
  price: string
  billingCycle: 'monthly' | 'annual' | 'one_time'
  features: string
  scoreRefreshFrequency: string
  documentUploadLimit: string
  scoreHistoryDepth: string
  aiAnalysisLevel: 'none' | 'basic' | 'advanced' | 'full'
  exportPermissions: string
  monthlyLookups: string
  apiRateLimit: string
  isActive: boolean
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const TABS: { key: TabType; label: string; icon: React.ElementType }[] = [
  { key: 'plans', label: 'Plans', icon: CreditCard },
  { key: 'assign', label: 'Assign Plan', icon: UserPlus },
  { key: 'analytics', label: 'Analytics', icon: BarChart3 },
  { key: 'rules', label: 'Rules', icon: Settings },
]

const AI_LEVELS = [
  { value: 'none', label: 'None' },
  { value: 'basic', label: 'Basic' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'full', label: 'Full' },
]

const BILLING_CYCLES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
  { value: 'one_time', label: 'One Time' },
]

const EMPTY_FORM: PlanFormData = {
  name: '',
  userType: 'vendor',
  price: '0',
  billingCycle: 'monthly',
  features: '',
  scoreRefreshFrequency: 'monthly',
  documentUploadLimit: '0',
  scoreHistoryDepth: '3',
  aiAnalysisLevel: 'none',
  exportPermissions: '',
  monthlyLookups: '',
  apiRateLimit: '',
  isActive: true,
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function AdminSubscriptions() {
  const [activeTab, setActiveTab] = useState<TabType>('plans')

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <CreditCard className="h-7 w-7 text-blue-600 dark:text-blue-400" />
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Subscription Manager
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage subscription plans, assignments, analytics, and rules for vendors and lenders
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-1 -mb-px" aria-label="Subscription tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-500'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'plans' && <PlansTab />}
      {activeTab === 'assign' && <AssignPlanTab />}
      {activeTab === 'analytics' && <AnalyticsTab />}
      {activeTab === 'rules' && <RulesTab />}
    </div>
  )
}

// ─── Plans Tab ─────────────────────────────────────────────────────────────────

function PlansTab() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null)
  const [formData, setFormData] = useState<PlanFormData>(EMPTY_FORM)
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [filterUserType, setFilterUserType] = useState<'' | 'vendor' | 'lender'>('')

  const fetchPlans = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, any> = {}
      if (filterUserType) params.userType = filterUserType
      const response = await adminService.listPlans(params)
      const data = response.data
      setPlans(Array.isArray(data) ? data : data.items || data.plans || [])
    } catch (err: any) {
      setError(err.message || 'Failed to load subscription plans')
      setPlans([])
    } finally {
      setLoading(false)
    }
  }, [filterUserType])

  useEffect(() => {
    fetchPlans()
  }, [fetchPlans])

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [successMsg])

  const openCreateForm = () => {
    setEditingPlan(null)
    setFormData(EMPTY_FORM)
    setFormError(null)
    setShowForm(true)
  }

  const openEditForm = (plan: SubscriptionPlan) => {
    setEditingPlan(plan)
    setFormData({
      name: plan.name,
      userType: plan.userType,
      price: String(plan.price),
      billingCycle: plan.billingCycle,
      features: (plan.features || []).join(', '),
      scoreRefreshFrequency: plan.scoreRefreshFrequency || 'monthly',
      documentUploadLimit: String(plan.documentUploadLimit || 0),
      scoreHistoryDepth: String(plan.scoreHistoryDepth || 3),
      aiAnalysisLevel: plan.aiAnalysisLevel || 'none',
      exportPermissions: (plan.exportPermissions || []).join(', '),
      monthlyLookups: plan.monthlyLookups != null ? String(plan.monthlyLookups) : '',
      apiRateLimit: plan.apiRateLimit != null ? String(plan.apiRateLimit) : '',
      isActive: plan.isActive,
    })
    setFormError(null)
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingPlan(null)
    setFormError(null)
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setFormError('Plan name is required')
      return
    }
    if (Number(formData.price) < 0) {
      setFormError('Price cannot be negative')
      return
    }

    setFormLoading(true)
    setFormError(null)
    try {
      const payload: Record<string, any> = {
        name: formData.name.trim(),
        userType: formData.userType,
        price: parseFloat(formData.price) || 0,
        billingCycle: formData.billingCycle,
        features: formData.features
          .split(',')
          .map((f) => f.trim())
          .filter(Boolean),
        scoreRefreshFrequency: formData.scoreRefreshFrequency,
        documentUploadLimit: parseInt(formData.documentUploadLimit) || 0,
        scoreHistoryDepth: parseInt(formData.scoreHistoryDepth) || 3,
        aiAnalysisLevel: formData.aiAnalysisLevel,
        exportPermissions: formData.exportPermissions
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean),
        monthlyLookups: formData.monthlyLookups ? parseInt(formData.monthlyLookups) : null,
        apiRateLimit: formData.apiRateLimit ? parseInt(formData.apiRateLimit) : null,
        isActive: formData.isActive,
      }

      if (editingPlan) {
        await adminService.updatePlan(editingPlan.id, payload)
        setSuccessMsg(`Plan "${formData.name}" updated successfully`)
      } else {
        await adminService.createPlan(payload)
        setSuccessMsg(`Plan "${formData.name}" created successfully`)
      }
      closeForm()
      fetchPlans()
    } catch (err: any) {
      setFormError(err.message || 'Failed to save plan')
    } finally {
      setFormLoading(false)
    }
  }

  const getUserTypeBadge = (userType: string) => {
    if (userType === 'vendor') {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
    }
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
  }

  const formatPrice = (price: number, cycle: string) => {
    if (price === 0) return 'Free'
    const formatted = `$${price.toFixed(2)}`
    if (cycle === 'monthly') return `${formatted}/mo`
    if (cycle === 'annual') return `${formatted}/yr`
    return formatted
  }

  return (
    <div className="space-y-4">
      {/* Success notification */}
      {successMsg && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
          <p className="text-sm text-green-700 dark:text-green-300">{successMsg}</p>
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <select
            value={filterUserType}
            onChange={(e) => setFilterUserType(e.target.value as '' | 'vendor' | 'lender')}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Types</option>
            <option value="vendor">Vendor Plans</option>
            <option value="lender">Lender Plans</option>
          </select>
        </div>
        <button
          onClick={openCreateForm}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create Plan
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={fetchPlans}
            className="ml-auto inline-flex items-center gap-1 text-sm text-red-700 dark:text-red-300 hover:underline"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </button>
        </div>
      )}

      {/* Plans List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
          <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loading plans...</span>
        </div>
      ) : plans.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <CreditCard className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">No subscription plans found</p>
          <button
            onClick={openCreateForm}
            className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            Create your first plan
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`rounded-lg border bg-white dark:bg-gray-800 p-5 transition-shadow hover:shadow-md ${
                plan.isActive
                  ? 'border-gray-200 dark:border-gray-700'
                  : 'border-gray-200 dark:border-gray-700 opacity-60'
              }`}
            >
              {/* Plan Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {plan.name}
                  </h3>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${getUserTypeBadge(plan.userType)}`}
                  >
                    {plan.userType}
                  </span>
                </div>
                <button
                  onClick={() => openEditForm(plan)}
                  className="p-1.5 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-400 dark:hover:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
                  title="Edit plan"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
              </div>

              {/* Price */}
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                {formatPrice(plan.price, plan.billingCycle)}
              </p>

              {/* Details */}
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex justify-between">
                  <span>Billing:</span>
                  <span className="font-medium text-gray-900 dark:text-white capitalize">
                    {plan.billingCycle.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>AI Level:</span>
                  <span className="font-medium text-gray-900 dark:text-white capitalize">
                    {plan.aiAnalysisLevel}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Score Refresh:</span>
                  <span className="font-medium text-gray-900 dark:text-white capitalize">
                    {plan.scoreRefreshFrequency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Doc Uploads:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {plan.documentUploadLimit === 0 ? 'None' : plan.documentUploadLimit}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>History Depth:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {plan.scoreHistoryDepth} entries
                  </span>
                </div>
                {plan.monthlyLookups != null && (
                  <div className="flex justify-between">
                    <span>Monthly Lookups:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {plan.monthlyLookups}
                    </span>
                  </div>
                )}
                {plan.apiRateLimit != null && (
                  <div className="flex justify-between">
                    <span>API Rate Limit:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {plan.apiRateLimit}/hr
                    </span>
                  </div>
                )}
              </div>

              {/* Features */}
              {plan.features && plan.features.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                    Features
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {plan.features.slice(0, 5).map((feat, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      >
                        {feat}
                      </span>
                    ))}
                    {plan.features.length > 5 && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        +{plan.features.length - 5} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Status indicator */}
              {!plan.isActive && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                    Inactive
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeForm}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingPlan ? 'Edit Plan' : 'Create Plan'}
              </h2>
              <button
                onClick={closeForm}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {formError && (
                <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
              )}

              {/* Plan Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Plan Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Professional"
                />
              </div>

              {/* User Type & Billing Cycle */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    User Type
                  </label>
                  <select
                    value={formData.userType}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, userType: e.target.value as 'vendor' | 'lender' }))
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="vendor">Vendor</option>
                    <option value="lender">Lender</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Billing Cycle
                  </label>
                  <select
                    value={formData.billingCycle}
                    onChange={(e) =>
                      setFormData((f) => ({
                        ...f,
                        billingCycle: e.target.value as 'monthly' | 'annual' | 'one_time',
                      }))
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {BILLING_CYCLES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Price ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData((f) => ({ ...f, price: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Features (comma-separated)
                </label>
                <textarea
                  value={formData.features}
                  onChange={(e) => setFormData((f) => ({ ...f, features: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="score_view, recommendations, badge_progress, document_upload"
                />
              </div>

              {/* Score / Doc / History */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Score Refresh
                  </label>
                  <select
                    value={formData.scoreRefreshFrequency}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, scoreRefreshFrequency: e.target.value }))
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                    <option value="daily">Daily</option>
                    <option value="on_demand">On Demand</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Doc Upload Limit
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.documentUploadLimit}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, documentUploadLimit: e.target.value }))
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    History Depth
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.scoreHistoryDepth}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, scoreHistoryDepth: e.target.value }))
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* AI Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  AI Analysis Level
                </label>
                <select
                  value={formData.aiAnalysisLevel}
                  onChange={(e) =>
                    setFormData((f) => ({
                      ...f,
                      aiAnalysisLevel: e.target.value as 'none' | 'basic' | 'advanced' | 'full',
                    }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {AI_LEVELS.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Export Permissions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Export Permissions (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.exportPermissions}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, exportPermissions: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="csv, xlsx, pdf"
                />
              </div>

              {/* Lender-specific fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Monthly Lookups
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.monthlyLookups}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, monthlyLookups: e.target.value }))
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Unlimited if empty"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    API Rate Limit (/hr)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.apiRateLimit}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, apiRateLimit: e.target.value }))
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Unlimited if empty"
                  />
                </div>
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active
                </label>
                <button
                  type="button"
                  onClick={() => setFormData((f) => ({ ...f, isActive: !f.isActive }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.isActive ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Form Actions */}
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
              <button
                onClick={closeForm}
                disabled={formLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={formLoading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {formLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {editingPlan ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Assign Plan Tab ───────────────────────────────────────────────────────────

function AssignPlanTab() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [userId, setUserId] = useState('')
  const [selectedPlanId, setSelectedPlanId] = useState('')
  const [loading, setLoading] = useState(false)
  const [plansLoading, setPlansLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  useEffect(() => {
    const loadPlans = async () => {
      setPlansLoading(true)
      try {
        const response = await adminService.listPlans()
        const data = response.data
        setPlans(Array.isArray(data) ? data : data.items || data.plans || [])
      } catch (err: any) {
        setError(err.message || 'Failed to load plans')
      } finally {
        setPlansLoading(false)
      }
    }
    loadPlans()
  }, [])

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [successMsg])

  const handleAssign = async () => {
    if (!userId.trim()) {
      setError('User ID is required')
      return
    }
    if (!selectedPlanId) {
      setError('Please select a plan')
      return
    }

    setLoading(true)
    setError(null)
    try {
      await adminService.assignPlan(userId.trim(), selectedPlanId)
      const plan = plans.find((p) => p.id === selectedPlanId)
      setSuccessMsg(`Plan "${plan?.name || selectedPlanId}" assigned to user ${userId.trim()}`)
      setUserId('')
      setSelectedPlanId('')
    } catch (err: any) {
      setError(err.message || 'Failed to assign plan')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-xl space-y-6">
      {/* Success notification */}
      {successMsg && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
          <p className="text-sm text-green-700 dark:text-green-300">{successMsg}</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-5">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Assign Plan to User
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Select a user and a plan to assign. This will immediately apply the new feature access limits.
        </p>

        {/* User ID input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            User ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter user ID (UUID)"
          />
        </div>

        {/* Plan selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Subscription Plan <span className="text-red-500">*</span>
          </label>
          {plansLoading ? (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              <span className="text-sm text-gray-400">Loading plans...</span>
            </div>
          ) : (
            <select
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a plan...</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} ({plan.userType}) — ${plan.price}/{plan.billingCycle}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Selected plan preview */}
        {selectedPlanId && (
          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            {(() => {
              const plan = plans.find((p) => p.id === selectedPlanId)
              if (!plan) return null
              return (
                <div className="text-sm space-y-1">
                  <p className="font-medium text-blue-900 dark:text-blue-200">
                    {plan.name} — {plan.userType}
                  </p>
                  <p className="text-blue-700 dark:text-blue-300">
                    ${plan.price}/{plan.billingCycle} • AI: {plan.aiAnalysisLevel} • Refresh:{' '}
                    {plan.scoreRefreshFrequency}
                  </p>
                  {plan.features.length > 0 && (
                    <p className="text-blue-600 dark:text-blue-400">
                      Features: {plan.features.join(', ')}
                    </p>
                  )}
                </div>
              )
            })()}
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleAssign}
          disabled={loading || !userId.trim() || !selectedPlanId}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <UserPlus className="h-4 w-4" />
          )}
          Assign Plan
        </button>
      </div>
    </div>
  )
}

// ─── Analytics Tab ─────────────────────────────────────────────────────────────

function AnalyticsTab() {
  const [analytics, setAnalytics] = useState<SubscriptionAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await adminService.getSubscriptionAnalytics()
        setAnalytics(response.data)
      } catch (err: any) {
        setError(err.message || 'Failed to load subscription analytics')
      } finally {
        setLoading(false)
      }
    }
    fetchAnalytics()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
        <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loading analytics...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
        <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="text-center py-16 text-gray-500 dark:text-gray-400">
        No analytics data available
      </div>
    )
  }

  const totalVendors = Object.values(analytics.vendorsPerTier || {}).reduce(
    (sum, v) => sum + v,
    0
  )
  const totalRevenue = Object.values(analytics.revenueByTier || {}).reduce(
    (sum, v) => sum + v,
    0
  )

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Vendors */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Subscribers</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalVendors}</p>
            </div>
          </div>
        </div>

        {/* Total Revenue */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
              <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ${totalRevenue.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Churn Rate */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
              <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Churn Rate</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {(analytics.churnRate * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>

        {/* Monthly Changes */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Net Changes (Last Month)</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {analytics.monthlyChanges && analytics.monthlyChanges.length > 0
                  ? `+${analytics.monthlyChanges[analytics.monthlyChanges.length - 1].upgrades} / -${analytics.monthlyChanges[analytics.monthlyChanges.length - 1].downgrades}`
                  : '0'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Vendors Per Tier */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Vendors Per Tier
        </h3>
        {Object.keys(analytics.vendorsPerTier || {}).length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No tier data available</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(analytics.vendorsPerTier).map(([tier, count]) => {
              const percentage = totalVendors > 0 ? (count / totalVendors) * 100 : 0
              return (
                <div key={tier}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                      {tier}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {count} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                      className="h-2 rounded-full bg-blue-600 dark:bg-blue-400 transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Revenue By Tier */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Revenue By Tier
        </h3>
        {Object.keys(analytics.revenueByTier || {}).length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No revenue data available</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    Tier
                  </th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    Revenue
                  </th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    % of Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {Object.entries(analytics.revenueByTier).map(([tier, revenue]) => (
                  <tr key={tier}>
                    <td className="px-4 py-2 font-medium text-gray-900 dark:text-white capitalize">
                      {tier}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                      ${revenue.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-500 dark:text-gray-400">
                      {totalRevenue > 0 ? ((revenue / totalRevenue) * 100).toFixed(1) : '0'}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Monthly Changes */}
      {analytics.monthlyChanges && analytics.monthlyChanges.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Monthly Plan Changes
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    Month
                  </th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    Upgrades
                  </th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    Downgrades
                  </th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                    Net
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {analytics.monthlyChanges.map((change, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">
                      {change.month}
                    </td>
                    <td className="px-4 py-2 text-right text-green-600 dark:text-green-400">
                      +{change.upgrades}
                    </td>
                    <td className="px-4 py-2 text-right text-red-600 dark:text-red-400">
                      -{change.downgrades}
                    </td>
                    <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-white">
                      {change.upgrades - change.downgrades >= 0 ? '+' : ''}
                      {change.upgrades - change.downgrades}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Rules Tab ─────────────────────────────────────────────────────────────────

function RulesTab() {
  const [rules, setRules] = useState<SubscriptionRule[]>([
    {
      ruleType: 'trial_duration',
      config: { days: 14 },
      isActive: true,
    },
    {
      ruleType: 'grace_period',
      config: { days: 3 },
      isActive: true,
    },
    {
      ruleType: 'auto_downgrade',
      config: { targetPlan: 'Free', enabled: true },
      isActive: true,
    },
    {
      ruleType: 'notifications',
      config: {
        approachingLimit: true,
        expiryWarningDays: 7,
        expired: true,
      },
      isActive: true,
    },
  ])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [successMsg])

  const updateRule = (index: number, config: Record<string, any>) => {
    setRules((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], config: { ...updated[index].config, ...config } }
      return updated
    })
    setDirty(true)
  }

  const toggleRule = (index: number) => {
    setRules((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], isActive: !updated[index].isActive }
      return updated
    })
    setDirty(true)
  }

  const handleSaveRules = async () => {
    setSaving(true)
    setError(null)
    try {
      // Use a generic PUT call through the admin service pattern
      const { apiClient } = await import('../../services/apiClient')
      await apiClient.put('/admin/subscriptions/rules', { rules })
      setSuccessMsg('Subscription rules saved successfully')
      setDirty(false)
    } catch (err: any) {
      setError(err.message || 'Failed to save rules')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Success */}
      {successMsg && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
          <p className="text-sm text-green-700 dark:text-green-300">{successMsg}</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Trial Duration */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Trial Duration
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              How long new users get trial access before requiring a subscription
            </p>
          </div>
          <button
            type="button"
            onClick={() => toggleRule(0)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              rules[0].isActive ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                rules[0].isActive ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600 dark:text-gray-400">Days:</label>
          <input
            type="number"
            min="1"
            value={rules[0].config.days}
            onChange={(e) => updateRule(0, { days: parseInt(e.target.value) || 14 })}
            className="w-24 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Grace Period */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Grace Period After Expiry
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Number of days after subscription expiry before access is revoked
            </p>
          </div>
          <button
            type="button"
            onClick={() => toggleRule(1)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              rules[1].isActive ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                rules[1].isActive ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600 dark:text-gray-400">Days:</label>
          <input
            type="number"
            min="0"
            value={rules[1].config.days}
            onChange={(e) => updateRule(1, { days: parseInt(e.target.value) || 3 })}
            className="w-24 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Auto-Downgrade */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Auto-Downgrade Behavior
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Automatically downgrade users to the Free plan when their subscription expires
            </p>
          </div>
          <button
            type="button"
            onClick={() => toggleRule(2)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              rules[2].isActive ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                rules[2].isActive ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600 dark:text-gray-400">Target Plan:</label>
          <input
            type="text"
            value={rules[2].config.targetPlan}
            onChange={(e) => updateRule(2, { targetPlan: e.target.value })}
            className="w-40 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Notification Triggers */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Notification Triggers
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Configure when users receive notifications about their subscription
            </p>
          </div>
          <button
            type="button"
            onClick={() => toggleRule(3)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              rules[3].isActive ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                rules[3].isActive ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={rules[3].config.approachingLimit}
              onChange={(e) => updateRule(3, { approachingLimit: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label className="text-sm text-gray-700 dark:text-gray-300">
              Notify when approaching usage limit
            </label>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600 dark:text-gray-400">
              Expiry warning (days before):
            </label>
            <input
              type="number"
              min="1"
              value={rules[3].config.expiryWarningDays}
              onChange={(e) =>
                updateRule(3, { expiryWarningDays: parseInt(e.target.value) || 7 })
              }
              className="w-20 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={rules[3].config.expired}
              onChange={(e) => updateRule(3, { expired: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label className="text-sm text-gray-700 dark:text-gray-300">
              Notify when subscription expires
            </label>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveRules}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Save Rules
        </button>
      </div>
    </div>
  )
}
